import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Wrapper from "../../components/wrapper/Wrapper";
import {
  FiTarget,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiX,
  FiSave,
  FiX as FiClose,
} from "react-icons/fi";
import {
  getAdminUserGoalsAPI,
  createAdminUserGoalAPI,
  updateAdminGoalAPI,
  deleteAdminGoalAPI,
  updateAdminTaskStatusAPI,
} from "../../API/AdminGoalsAPI";
import { getSocialMediaPlatformsAPI } from "../../API/SocialMediaAPI";
import {
  getAdminUserAthletePointsAPI,
  updateAdminUserAthletePointsAPI,
} from "../../API/AthletePointsAPI";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ButtonLoader from "../../components/button/ButtonLoader";
import moment from "moment";

const UserGoals = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const userName = searchParams.get("name") || "User";
  const queryClient = useQueryClient();

  const [editingGoal, setEditingGoal] = useState(null);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showPointsBreakdown, setShowPointsBreakdown] = useState(false);
  const [actionLoading, setActionLoading] = useState({ type: null, id: null });
  const [editingPoints, setEditingPoints] = useState({});
  const [pointsUpdateLoading, setPointsUpdateLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingProofLink, setEditingProofLink] = useState({});
  const [showProofLinkModal, setShowProofLinkModal] = useState({});
  const [newGoal, setNewGoal] = useState({
    socialMediaPlatform: "",
    growthFocus: 0,
    growthPercentage: 0,
    timelineForAchievement: 4,
    postingFrequency: 1,
    tasks: [
      {
        Week: 1,
        Tasks: [{ target: "" }],
      },
    ],
  });

  // Fetch user goals
  const {
    data: goalsData,
    isLoading: goalsLoading,
    error: goalsError,
    refetch,
  } = useQuery({
    queryKey: ["adminUserGoals", userId],
    queryFn: () => getAdminUserGoalsAPI(userId),
    enabled: !!userId,
  });

  // Fetch social media platforms
  const {
    data: socialMediaData,
    isLoading: socialMediaLoading,
    error: socialMediaError,
  } = useQuery({
    queryKey: ["socialMediaPlatforms"],
    queryFn: () => getSocialMediaPlatformsAPI(),
  });

  // Fetch user athlete points
  const { data: athletePointsData, isLoading: athletePointsLoading } = useQuery(
    {
      queryKey: ["adminUserAthletePoints", userId],
      queryFn: () => getAdminUserAthletePointsAPI(userId),
      enabled: !!userId,
    }
  );

  // Debug social media data
  console.log("Social Media Debug:", {
    socialMediaData,
    socialMediaLoading,
    socialMediaError,
  });

  const handleUpdateTaskStatus = async (
    goalId,
    week,
    taskIndex,
    isComplete,
    proofLink = ""
  ) => {
    setActionLoading({ type: "task", id: `${goalId}-${week}-${taskIndex}` });
    try {
      await updateAdminTaskStatusAPI(goalId, {
        week,
        taskIndex,
        isComplete,
        proofLink,
      });
      queryClient.invalidateQueries(["adminUserGoals", userId]);
    } catch (error) {
      console.error("Error updating task status:", error);
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const handleDeleteTask = async (goalId, week, taskIndex) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    setActionLoading({
      type: "deleteTask",
      id: `${goalId}-${week}-${taskIndex}`,
    });
    try {
      // Get the current goal data
      const currentGoals = queryClient.getQueryData(["adminUserGoals", userId]);
      const goal = currentGoals?.data?.find((g) => g._id === goalId);

      if (!goal) {
        alert("Goal not found");
        return;
      }

      // Create updated tasks with the task removed
      const updatedTasks = goal.tasks.map((weekTask) => {
        if (weekTask.Week === week) {
          const updatedWeekTasks = weekTask.Tasks.filter(
            (_, index) => index !== taskIndex
          );
          return {
            Week: weekTask.Week,
            Tasks: updatedWeekTasks.map((task) => ({
              target: task.target,
              isComplete: task.isComplete,
              // Don't include _id field
            })),
          };
        }
        return {
          Week: weekTask.Week,
          Tasks: weekTask.Tasks.map((task) => ({
            target: task.target,
            isComplete: task.isComplete,
            // Don't include _id field
          })),
        };
      });

      // Update the goal with the new tasks
      await updateAdminGoalAPI(goalId, {
        tasks: updatedTasks,
      });

      queryClient.invalidateQueries(["adminUserGoals", userId]);
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("Failed to delete task: " + (error.message || "Unknown error"));
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("Are you sure you want to delete this goal?")) return;

    setActionLoading({ type: "delete", id: goalId });
    try {
      await deleteAdminGoalAPI(goalId);
      queryClient.invalidateQueries(["adminUserGoals", userId]);
    } catch (error) {
      console.error("Error deleting goal:", error);
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const handleUpdateGoal = async () => {
    if (!editingGoal) return;

    // Validate required fields
    if (!editingGoal.socialMediaPlatform) {
      alert("Please select a social media platform");
      return;
    }

    // Validate tasks
    const hasValidTasks = editingGoal.tasks.some((week) =>
      week.Tasks.some((task) => task.target.trim() !== "")
    );

    if (!hasValidTasks) {
      alert("Please add at least one task");
      return;
    }

    setActionLoading({ type: "update", id: editingGoal._id });
    try {
      // Clean tasks data - remove _id fields that validation doesn't allow
      const cleanedTasks = editingGoal.tasks.map((week) => ({
        Week: week.Week,
        Tasks: week.Tasks.map((task) => ({
          target: task.target,
          isComplete: task.isComplete,
          // Don't include _id field
        })),
      }));

      await updateAdminGoalAPI(editingGoal._id, {
        socialMediaPlatform:
          typeof editingGoal.socialMediaPlatform === "string"
            ? editingGoal.socialMediaPlatform
            : editingGoal.socialMediaPlatform?._id ||
              editingGoal.socialMediaPlatform,
        growthFocus: editingGoal.growthFocus,
        growthPercentage: editingGoal.growthPercentage,
        timelineForAchievement: editingGoal.timelineForAchievement,
        postingFrequency: editingGoal.postingFrequency,
        tasks: cleanedTasks,
      });
      queryClient.invalidateQueries(["adminUserGoals", userId]);
      setEditingGoal(null);
    } catch (error) {
      console.error("Error updating goal:", error);
      alert("Failed to update goal: " + (error.message || "Unknown error"));
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.socialMediaPlatform) {
      alert("Please select a social media platform");
      return;
    }

    // Validate tasks
    const hasValidTasks = newGoal.tasks.some((week) =>
      week.Tasks.some((task) => task.target.trim() !== "")
    );

    if (!hasValidTasks) {
      alert("Please add at least one task");
      return;
    }

    setActionLoading({ type: "create", id: "new" });
    try {
      // Remove isComplete field from tasks when creating - it's added by backend
      const goalDataToSend = {
        ...newGoal,
        tasks: newGoal.tasks.map((week) => ({
          Week: week.Week,
          Tasks: week.Tasks.map((task) => ({
            target: task.target,
            // Don't include isComplete when creating
          })),
        })),
      };

      await createAdminUserGoalAPI(userId, goalDataToSend);
      queryClient.invalidateQueries(["adminUserGoals", userId]);
      setShowAddGoal(false);
      setNewGoal({
        socialMediaPlatform: "",
        growthFocus: 0,
        growthPercentage: 0,
        timelineForAchievement: 4,
        postingFrequency: 1,
        tasks: [
          {
            Week: 1,
            Tasks: [{ target: "" }],
          },
        ],
      });
    } catch (error) {
      console.error("Error creating goal:", error);
      alert("Failed to create goal: " + (error.message || "Unknown error"));
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const getGrowthFocusText = (focus) => {
    const focusMap = {
      0: "Followers",
      1: "Reach",
      2: "Engagement",
      3: "Subscribers",
    };
    return focusMap[focus] || "Unknown";
  };

  const getSocialMediaName = (platform) => {
    return platform?.name || "Unknown Platform";
  };

  // Helper function to render text with bold formatting
  const renderTextWithBold = (text) => {
    if (!text || !text.includes("**")) {
      return text;
    }

    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, partIndex) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        const boldText = part.slice(2, -2);
        return (
          <strong key={partIndex} className="font-bold text-gray-900">
            {boldText}
          </strong>
        );
      }
      return <span key={partIndex}>{part}</span>;
    });
  };

  const formatTaskContent = (content) => {
    if (!content) return "";

    // Split content into lines for processing
    const lines = content.split("\n").filter((line) => line.trim() !== "");

    return lines.map((line, index) => {
      const trimmedLine = line.trim();

      // Handle bullet points (sub-tasks and detailed steps)
      if (trimmedLine.startsWith("-")) {
        const bulletText = trimmedLine.substring(1).trim();
        return (
          <div key={index} className="ml-4 flex items-start space-x-2">
            <span className="text-gray-600 mt-1">•</span>
            <span className="text-gray-700 text-sm">
              {renderTextWithBold(bulletText)}
            </span>
          </div>
        );
      }

      // Handle headings (### text)
      if (trimmedLine.startsWith("###")) {
        const headingText = trimmedLine.substring(3).trim();
        return (
          <div key={index} className="mb-2 mt-3">
            <h4 className="text-base font-bold text-gray-900 border-b border-gray-200 pb-1">
              {renderTextWithBold(headingText)}
            </h4>
          </div>
        );
      }

      // Handle bold text (**text**)
      if (trimmedLine.includes("**")) {
        return (
          <div key={index} className="mb-2 text-gray-700">
            {renderTextWithBold(trimmedLine)}
          </div>
        );
      }

      // Regular text (task titles and descriptions)
      return (
        <div key={index} className="text-gray-700 text-sm mb-1">
          {trimmedLine}
        </div>
      );
    });
  };

  const handleOpenProofLinkModal = (
    goalId,
    week,
    taskIndex,
    currentProofLink = ""
  ) => {
    const modalKey = `${goalId}-${week}-${taskIndex}`;
    setShowProofLinkModal({ [modalKey]: true });
    setEditingProofLink({ [modalKey]: currentProofLink });
  };

  const handleCloseProofLinkModal = (goalId, week, taskIndex) => {
    const modalKey = `${goalId}-${week}-${taskIndex}`;
    setShowProofLinkModal((prev) => {
      const newState = { ...prev };
      delete newState[modalKey];
      return newState;
    });
    setEditingProofLink((prev) => {
      const newState = { ...prev };
      delete newState[modalKey];
      return newState;
    });
  };

  const handleSaveProofLink = async (goalId, week, taskIndex) => {
    const modalKey = `${goalId}-${week}-${taskIndex}`;
    const proofLink = editingProofLink[modalKey] || "";

    // Get current task to check if it's complete
    const currentGoals = queryClient.getQueryData(["adminUserGoals", userId]);
    const goal = currentGoals?.data?.find((g) => g._id === goalId);
    const weekTask = goal?.tasks?.find((w) => w.Week === week);
    const task = weekTask?.Tasks?.[taskIndex];

    await handleUpdateTaskStatus(
      goalId,
      week,
      taskIndex,
      task?.isComplete || false,
      proofLink
    );
    handleCloseProofLinkModal(goalId, week, taskIndex);
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handlePointsUpdate = async () => {
    if (Object.keys(editingPoints).length === 0) {
      alert("No changes to save");
      return;
    }

    // Convert string values to numbers for API call
    const numericPoints = {};
    for (const [field, value] of Object.entries(editingPoints)) {
      if (value !== "" && value !== null && value !== undefined) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          numericPoints[field] = numValue;
        }
      }
    }

    // Check if there are any valid numeric values to send
    if (Object.keys(numericPoints).length === 0) {
      alert("No valid numeric values to save");
      return;
    }

    setPointsUpdateLoading(true);
    try {
      await updateAdminUserAthletePointsAPI(userId, numericPoints);

      // Force refetch the data and reinitialize the form
      await queryClient.refetchQueries(["adminUserAthletePoints", userId]);

      // Get the fresh data and reinitialize the form
      const updatedData = queryClient.getQueryData([
        "adminUserAthletePoints",
        userId,
      ]);
      if (updatedData) {
        initializeEditingPoints(updatedData);
      }

      alert("Points updated successfully!");
    } catch (error) {
      console.error("Error updating points:", error);
      alert("Failed to update points: " + (error.message || "Unknown error"));
    } finally {
      setPointsUpdateLoading(false);
    }
  };

  const handlePointsChange = (field, value) => {
    // Always update the field value first (allow typing)
    setEditingPoints((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Handle empty string - allow it to stay empty while typing
    if (value === "" || value === null || value === undefined) {
      // Clear error for empty values
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      return;
    }

    // Parse the value as a number (including decimals)
    const numValue = parseFloat(value);

    // Check if the parsed value is a valid number
    if (isNaN(numValue)) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "Please enter a valid number",
      }));
      return;
    }

    // Define maximum values for each field
    const maxValues = {
      "socialMedia.follower.point": 12,
      "socialMedia.engagementRate.points": 8,
      "socialMedia.PlatformDiversity.points": 4,
      "socialMedia.FeedPostsThisWeek.points": 6,
      "socialMedia.StoryPostsThisWeek.points": 4,
      "socialMedia.WeeklyPostCompletion.points": 8,
      "socialMedia.WeeklyGoalCompletion.points": 10,
      "BrandPartnerships.PaidDealValue.point": 50,
      "BrandPartnerships.FreeDeals": 3,
      "MerchSales.UnitsSoldThisWeek.point": 15,
      "MerchSales.TopSellerBonus.point": 10,
      "AIToolUsage.PersonalBrandingAITool.point": 10,
      "AIToolUsage.PartnershipsAITool.point": 7.5,
      "AIToolUsage.MerchAITool.point": 7.5,
    };

    // Check if value exceeds maximum
    const maxValue = maxValues[field];
    if (numValue > maxValue) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: `Maximum value allowed is ${maxValue}`,
      }));
      return;
    } else {
      // Clear error if value is valid
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const renderInputField = (field, label, maxValue) => (
    <div className="flex items-center justify-between">
      <label className="text-sm font-medium text-gray-700">{label}:</label>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={editingPoints[field] ?? ""}
            onChange={(e) => handlePointsChange(field, e.target.value)}
            className={`w-16 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center ${
              fieldErrors[field]
                ? "border-red-500 bg-red-50"
                : "border-gray-300"
            }`}
            placeholder="0"
          />
          <span className="text-xs text-gray-500">/{maxValue}</span>
        </div>
        {fieldErrors[field] && (
          <span className="text-xs text-red-500">{fieldErrors[field]}</span>
        )}
      </div>
    </div>
  );

  const initializeEditingPoints = (pointsData) => {
    if (!pointsData?.data) return;

    const points = pointsData.data;
    setEditingPoints({
      "socialMedia.follower.point": points.socialMedia?.follower?.point ?? "",
      "socialMedia.engagementRate.points":
        points.socialMedia?.engagementRate?.points ?? "",
      "socialMedia.PlatformDiversity.points":
        points.socialMedia?.PlatformDiversity?.points ?? "",
      "socialMedia.FeedPostsThisWeek.points":
        points.socialMedia?.FeedPostsThisWeek?.points ?? "",
      "socialMedia.StoryPostsThisWeek.points":
        points.socialMedia?.StoryPostsThisWeek?.points ?? "",
      "socialMedia.WeeklyPostCompletion.points":
        points.socialMedia?.WeeklyPostCompletion?.points ?? "",
      "socialMedia.WeeklyGoalCompletion.points":
        points.socialMedia?.WeeklyGoalCompletion?.points ?? "",
      "BrandPartnerships.PaidDealValue.point":
        points.BrandPartnerships?.PaidDealValue?.point ?? "",
      "BrandPartnerships.FreeDeals": points.BrandPartnerships?.FreeDeals ?? "",
      "MerchSales.UnitsSoldThisWeek.point":
        points.MerchSales?.UnitsSoldThisWeek?.point ?? "",
      "MerchSales.TopSellerBonus.point":
        points.MerchSales?.TopSellerBonus?.point ?? "",
      "AIToolUsage.PersonalBrandingAITool.point":
        points.AIToolUsage?.PersonalBrandingAITool?.point ?? "",
      "AIToolUsage.PartnershipsAITool.point":
        points.AIToolUsage?.PartnershipsAITool?.point ?? "",
      "AIToolUsage.MerchAITool.point":
        points.AIToolUsage?.MerchAITool?.point ?? "",
    });
  };

  function calculateTotalPercentage(athletePointDoc, useEditingPoints = false) {
    console.log("athletePointDoc", athletePointDoc);

    // Use editing points if available and requested
    const getValue = (path, defaultValue = 0) => {
      if (useEditingPoints && editingPoints[path] !== undefined) {
        const value = editingPoints[path];
        // Handle empty string or null values
        if (value === "" || value === null || value === undefined) {
          return defaultValue;
        }
        return value;
      }
      return (
        path.split(".").reduce((obj, key) => obj?.[key], athletePointDoc) ||
        defaultValue
      );
    };

    let socialRaw =
      getValue("socialMedia.follower.point") +
      getValue("socialMedia.engagementRate.points") +
      getValue("socialMedia.PlatformDiversity.points") +
      getValue("socialMedia.FeedPostsThisWeek.points") +
      getValue("socialMedia.StoryPostsThisWeek.points") +
      getValue("socialMedia.WeeklyPostCompletion.points") +
      getValue("socialMedia.WeeklyGoalCompletion.points");

    let brandRaw =
      getValue("BrandPartnerships.PaidDealValue.point") +
      getValue("BrandPartnerships.FreeDeals");

    let merchRaw =
      getValue("MerchSales.UnitsSoldThisWeek.point") +
      getValue("MerchSales.TopSellerBonus.point");

    let aiRaw =
      getValue("AIToolUsage.PersonalBrandingAITool.point") +
      getValue("AIToolUsage.PartnershipsAITool.point") +
      getValue("AIToolUsage.MerchAITool.point");

    // Max values from your table
    const socialMax = 52;
    const brandMax = 53;
    const merchMax = 25;
    const aiMax = 25;

    // Weighted scores
    let socialScore = (socialRaw / socialMax) * 40;
    let brandScore = (brandRaw / brandMax) * 30;
    let merchScore = (merchRaw / merchMax) * 15;
    let aiScore = (aiRaw / aiMax) * 15;

    // Total (out of 100)
    let total = socialScore + brandScore + merchScore + aiScore;

    const percentage = Number(total.toFixed(2)); // return only number
    // round to nearest integer
    return Math.round(percentage);
  }

  if (goalsLoading) {
    return (
      <Wrapper>
        <div className="py-12 w-full flex justify-center items-center">
          <ButtonLoader />
        </div>
      </Wrapper>
    );
  }

  if (goalsError) {
    return (
      <Wrapper>
        <div className="py-12 w-full flex flex-col justify-center items-center text-gray-500">
          <FiTarget className="w-12 h-12 mb-4 text-red-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error Loading Goals
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {goalsError?.message || "Failed to load goals"}
          </p>
          <button
            onClick={() => refetch()}
            className="bg-[--primary] hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Wrapper>
    );
  }

  const goals = goalsData?.data || [];

  return (
    <Wrapper>
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Goals for {userName}
            </h1>
            <p className="text-gray-500 text-sm">
              Manage goals and track progress for this user
            </p>
            {!athletePointsLoading && athletePointsData && (
              <div className="mt-2 flex items-center space-x-4">
                <div className="bg-blue-50 px-3 py-1 rounded-full">
                  <span className="text-blue-700 text-sm font-medium">
                    Total Score:
                    {` ${calculateTotalPercentage(athletePointsData.data)}%`}
                  </span>
                </div>
                <button
                  onClick={() => {
                    initializeEditingPoints(athletePointsData);
                    setShowPointsBreakdown(true);
                  }}
                  className="bg-purple-50 hover:bg-purple-100 px-3 py-1 rounded-full transition-colors"
                >
                  <span className="text-purple-700 text-sm font-medium">
                    Edit Points
                  </span>
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowAddGoal(true)}
            className="bg-[--primary] hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add Goal
          </button>
        </div>
      </div>

      {/* Goals List */}
      {goals.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FiTarget className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No goals found
          </h3>
          <p className="text-gray-500 mb-4">
            This user hasn't created any goals yet.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setShowAddGoal(true)}
              className="bg-[--primary] hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Create First Goal
            </button>
            <button
              onClick={async () => {
                if (!socialMediaData?.data?.[0]?._id) {
                  alert("No social media platforms available");
                  return;
                }

                const sampleGoal = {
                  socialMediaPlatform: socialMediaData.data[0]._id,
                  growthFocus: 0,
                  growthPercentage: 50,
                  timelineForAchievement: 4,
                  postingFrequency: 3,
                  tasks: [
                    {
                      Week: 1,
                      Tasks: [
                        {
                          target:
                            "### Content Creation\n**Create Engaging Content**\n- Post 3 high-quality photos\n- Use natural lighting\n- Include lifestyle shots",
                        },
                        {
                          target:
                            "### Engagement Strategy\n**Increase Engagement**\n- Engage with 20 followers daily\n- Respond to comments within 2 hours\n- Like and comment on similar accounts",
                        },
                        {
                          target:
                            "### Hashtag Optimization\n**Research and Use**\n- Research trending hashtags\n- Use 15-20 relevant hashtags\n- Mix popular and niche tags",
                        },
                      ],
                    },
                  ],
                };

                setActionLoading({ type: "create", id: "sample" });
                try {
                  await createAdminUserGoalAPI(userId, sampleGoal);
                  queryClient.invalidateQueries(["adminUserGoals", userId]);
                } catch (error) {
                  console.error("Error creating sample goal:", error);
                  alert(
                    "Failed to create sample goal: " +
                      (error.message || "Unknown error")
                  );
                } finally {
                  setActionLoading({ type: null, id: null });
                }
              }}
              disabled={actionLoading.type === "create"}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {actionLoading.type === "create" &&
              actionLoading.id === "sample" ? (
                <ButtonLoader />
              ) : (
                <>
                  <FiTarget className="w-4 h-4" />
                  Create Sample Goal
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {goals.map((goal) => (
            <div
              key={goal._id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Goal Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {goal.socialMediaPlatform?.imageUrl && (
                      <img
                        src={goal.socialMediaPlatform.imageUrl}
                        alt={getSocialMediaName(goal.socialMediaPlatform)}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {getSocialMediaName(goal.socialMediaPlatform)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Focus: {getGrowthFocusText(goal.growthFocus)} • Target:{" "}
                        {goal.growthPercentage}% • Timeline:{" "}
                        {goal.timelineForAchievement} weeks
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingGoal(goal)}
                      className="p-2 text-gray-400 hover:text-[--primary] transition-colors"
                      title="Edit Goal"
                    >
                      <FiEdit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGoal(goal._id)}
                      disabled={
                        actionLoading.type === "delete" &&
                        actionLoading.id === goal._id
                      }
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                      title="Delete Goal"
                    >
                      {actionLoading.type === "delete" &&
                      actionLoading.id === goal._id ? (
                        <ButtonLoader />
                      ) : (
                        <FiTrash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Tasks by Week */}
              <div className="p-6">
                <div className="space-y-6">
                  {goal.tasks.map((weekTask, weekIndex) => (
                    <div
                      key={weekIndex}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <h4 className="text-md font-medium text-gray-900 mb-3">
                        Week {weekTask.Week}
                      </h4>
                      <div className="space-y-2">
                        {weekTask.Tasks.map((task, taskIndex) => (
                          <div
                            key={taskIndex}
                            className="p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="mb-3">
                              <div className="text-sm leading-relaxed">
                                {formatTaskContent(task.target)}
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleUpdateTaskStatus(
                                      goal._id,
                                      weekTask.Week,
                                      taskIndex,
                                      !task.isComplete,
                                      task.proofLink || ""
                                    )
                                  }
                                  disabled={
                                    actionLoading.type === "task" &&
                                    actionLoading.id ===
                                      `${goal._id}-${weekTask.Week}-${taskIndex}`
                                  }
                                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                                    task.isComplete
                                      ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                                      : "bg-green-100 text-green-700 hover:bg-green-200"
                                  }`}
                                >
                                  {actionLoading.type === "task" &&
                                  actionLoading.id ===
                                    `${goal._id}-${weekTask.Week}-${taskIndex}` ? (
                                    <ButtonLoader />
                                  ) : task.isComplete ? (
                                    "Mark as Pending"
                                  ) : (
                                    "Mark as Complete"
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleOpenProofLinkModal(
                                      goal._id,
                                      weekTask.Week,
                                      taskIndex,
                                      task.proofLink || ""
                                    )
                                  }
                                  className="px-3 py-1 text-xs font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                                  title="Manage proof link"
                                >
                                  {task.proofLink ? "Edit Proof" : "Add Proof"}
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteTask(
                                      goal._id,
                                      weekTask.Week,
                                      taskIndex
                                    )
                                  }
                                  disabled={
                                    actionLoading.type === "deleteTask" &&
                                    actionLoading.id ===
                                      `${goal._id}-${weekTask.Week}-${taskIndex}`
                                  }
                                  className="px-3 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 bg-red-100 text-red-700 hover:bg-red-200"
                                >
                                  {actionLoading.type === "deleteTask" &&
                                  actionLoading.id ===
                                    `${goal._id}-${weekTask.Week}-${taskIndex}` ? (
                                    <ButtonLoader />
                                  ) : (
                                    "Delete Task"
                                  )}
                                </button>
                                <div className="flex items-center space-x-2">
                                  <span
                                    className={`text-xs px-2 py-1 rounded-full ${
                                      task.isComplete
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {task.isComplete ? "Complete" : "Pending"}
                                  </span>
                                  {task.proofLink && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 flex items-center space-x-1">
                                      <span>📎</span>
                                      <span>Proof</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Proof Link Display */}
                            {task.proofLink && (
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <span className="text-sm text-blue-700 font-semibold flex-shrink-0">
                                      📎 Proof Link:
                                    </span>
                                    <a
                                      href={task.proofLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 hover:text-blue-800 underline truncate flex-1"
                                      title={task.proofLink}
                                    >
                                      {task.proofLink}
                                    </a>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleOpenProofLinkModal(
                                        goal._id,
                                        weekTask.Week,
                                        taskIndex,
                                        task.proofLink || ""
                                      )
                                    }
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium ml-2 flex-shrink-0"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Goal Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>
                    Created: {moment(goal.createdAt).format("MMM DD, YYYY")}
                  </span>
                  <span>
                    Posting Frequency: {goal.postingFrequency} posts/week
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Goal Modal */}
      {showAddGoal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowAddGoal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Create New Goal for {userName}
                  </h3>
                  <button
                    onClick={() => setShowAddGoal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-4 max-h-96 overflow-y-auto">
                <div className="space-y-4">
                  {/* Social Media Platform */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Social Media Platform
                    </label>
                    <select
                      value={newGoal.socialMediaPlatform}
                      onChange={(e) =>
                        setNewGoal({
                          ...newGoal,
                          socialMediaPlatform: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                      disabled={socialMediaLoading}
                    >
                      <option value="">Select Platform</option>
                      {socialMediaData?.data?.map((platform) => (
                        <option key={platform._id} value={platform._id}>
                          {platform.name}
                        </option>
                      ))}
                    </select>
                    {socialMediaError && (
                      <p className="text-red-500 text-sm mt-1">
                        Error loading platforms: {socialMediaError.message}
                      </p>
                    )}
                  </div>

                  {/* Growth Focus */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Growth Focus
                    </label>
                    <select
                      value={newGoal.growthFocus}
                      onChange={(e) =>
                        setNewGoal({
                          ...newGoal,
                          growthFocus: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                    >
                      <option value={0}>Followers</option>
                      <option value={1}>Reach</option>
                      <option value={2}>Engagement</option>
                      <option value={3}>Subscribers</option>
                    </select>
                  </div>

                  {/* Growth Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Growth Percentage (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1000"
                      value={newGoal.growthPercentage}
                      onChange={(e) =>
                        setNewGoal({
                          ...newGoal,
                          growthPercentage: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                      placeholder="Enter target percentage"
                    />
                  </div>

                  {/* Timeline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timeline (weeks)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="52"
                      value={newGoal.timelineForAchievement}
                      onChange={(e) =>
                        setNewGoal({
                          ...newGoal,
                          timelineForAchievement: parseInt(e.target.value) || 1,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                      placeholder="Enter timeline in weeks"
                    />
                  </div>

                  {/* Posting Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Posting Frequency (posts/week)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={newGoal.postingFrequency}
                      onChange={(e) =>
                        setNewGoal({
                          ...newGoal,
                          postingFrequency: parseInt(e.target.value) || 1,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                      placeholder="Enter posting frequency"
                    />
                  </div>

                  {/* Tasks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Week 1 Tasks
                    </label>
                    <div className="space-y-2">
                      {newGoal.tasks[0].Tasks.map((task, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="text"
                            value={task.target}
                            onChange={(e) => {
                              const updatedTasks = [...newGoal.tasks];
                              updatedTasks[0].Tasks[index].target =
                                e.target.value;
                              setNewGoal({ ...newGoal, tasks: updatedTasks });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                            placeholder="Enter task description (use ### for headings, **bold** for emphasis, - for bullet points)"
                          />
                          {newGoal.tasks[0].Tasks.length > 1 && (
                            <button
                              onClick={() => {
                                const updatedTasks = [...newGoal.tasks];
                                updatedTasks[0].Tasks.splice(index, 1);
                                setNewGoal({ ...newGoal, tasks: updatedTasks });
                              }}
                              className="p-2 text-red-600 hover:text-red-700"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const updatedTasks = [...newGoal.tasks];
                          updatedTasks[0].Tasks.push({
                            target: "",
                          });
                          setNewGoal({ ...newGoal, tasks: updatedTasks });
                        }}
                        className="text-[--primary] hover:text-orange-600 text-sm font-medium"
                      >
                        + Add Task
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddGoal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGoal}
                  disabled={actionLoading.type === "create"}
                  className="px-4 py-2 text-sm font-medium text-white bg-[--primary] border border-transparent rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading.type === "create" ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Create Goal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Goal Modal */}
      {editingGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit Goal -{" "}
                  {getSocialMediaName(editingGoal.socialMediaPlatform)}
                </h2>
                <button
                  onClick={() => setEditingGoal(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiClose className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Social Media Platform */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Social Media Platform
                  </label>
                  <select
                    value={editingGoal.socialMediaPlatform}
                    onChange={(e) =>
                      setEditingGoal({
                        ...editingGoal,
                        socialMediaPlatform: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                    disabled={socialMediaLoading}
                  >
                    <option value="">Select Platform</option>
                    {socialMediaData?.data?.map((platform) => (
                      <option key={platform._id} value={platform._id}>
                        {platform.name}
                      </option>
                    ))}
                  </select>
                  {socialMediaError && (
                    <p className="text-red-500 text-sm mt-1">
                      Error loading platforms: {socialMediaError.message}
                    </p>
                  )}
                </div>

                {/* Growth Focus */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Growth Focus
                  </label>
                  <select
                    value={editingGoal.growthFocus}
                    onChange={(e) =>
                      setEditingGoal({
                        ...editingGoal,
                        growthFocus: parseInt(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                  >
                    <option value={0}>Followers</option>
                    <option value={1}>Reach</option>
                    <option value={2}>Engagement</option>
                    <option value={3}>Subscribers</option>
                  </select>
                </div>

                {/* Growth Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Growth Percentage (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={editingGoal.growthPercentage}
                    onChange={(e) =>
                      setEditingGoal({
                        ...editingGoal,
                        growthPercentage: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                    placeholder="Enter target percentage"
                  />
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeline (weeks)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={editingGoal.timelineForAchievement}
                    onChange={(e) =>
                      setEditingGoal({
                        ...editingGoal,
                        timelineForAchievement: parseInt(e.target.value) || 1,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                    placeholder="Enter timeline in weeks"
                  />
                </div>

                {/* Posting Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Posting Frequency (posts/week)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={editingGoal.postingFrequency}
                    onChange={(e) =>
                      setEditingGoal({
                        ...editingGoal,
                        postingFrequency: parseInt(e.target.value) || 1,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                    placeholder="Enter posting frequency"
                  />
                </div>
              </div>

              {/* Tasks */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tasks
                </label>
                <div className="space-y-4">
                  {editingGoal.tasks.map((weekTask, weekIndex) => (
                    <div
                      key={weekIndex}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <h4 className="text-md font-medium text-gray-900 mb-3">
                        Week {weekTask.Week}
                      </h4>
                      <div className="space-y-2">
                        {weekTask.Tasks.map((task, taskIndex) => (
                          <div
                            key={taskIndex}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="text"
                              value={task.target}
                              onChange={(e) => {
                                const updatedTasks = [...editingGoal.tasks];
                                updatedTasks[weekIndex].Tasks[
                                  taskIndex
                                ].target = e.target.value;
                                setEditingGoal({
                                  ...editingGoal,
                                  tasks: updatedTasks,
                                });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                              placeholder="Enter task description (use ### for headings, **bold** for emphasis, - for bullet points)"
                            />
                            {weekTask.Tasks.length > 1 && (
                              <button
                                onClick={() => {
                                  const updatedTasks = [...editingGoal.tasks];
                                  updatedTasks[weekIndex].Tasks.splice(
                                    taskIndex,
                                    1
                                  );
                                  setEditingGoal({
                                    ...editingGoal,
                                    tasks: updatedTasks,
                                  });
                                }}
                                className="p-2 text-red-600 hover:text-red-700"
                              >
                                <FiX className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const updatedTasks = [...editingGoal.tasks];
                            updatedTasks[weekIndex].Tasks.push({
                              target: "",
                              isComplete: false,
                            });
                            setEditingGoal({
                              ...editingGoal,
                              tasks: updatedTasks,
                            });
                          }}
                          className="text-[--primary] hover:text-orange-600 text-sm font-medium"
                        >
                          + Add Task
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
              <button
                onClick={() => setEditingGoal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateGoal}
                disabled={actionLoading.type === "update"}
                className="px-4 py-2 text-sm font-medium text-white bg-[--primary] border border-transparent rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading.type === "update" ? (
                  <ButtonLoader />
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Update Goal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Points Breakdown Modal */}
      {showPointsBreakdown && athletePointsData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit Points for {userName}
                </h2>
                <button
                  onClick={() => {
                    setShowPointsBreakdown(false);
                    setEditingPoints({});
                    setFieldErrors({});
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiClose className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Total Points */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Total Score:{" "}
                    {calculateTotalPercentage(athletePointsData.data, true)}%
                  </h3>
                  <p className="text-sm text-gray-600">
                    This score updates in real-time as you modify the points
                    below
                  </p>
                </div>

                {/* Social Media (40%) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    📱 Social Media Activity (40% weight)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputField(
                      "socialMedia.follower.point",
                      "Follower Count",
                      "12"
                    )}
                    {renderInputField(
                      "socialMedia.engagementRate.points",
                      "Engagement Rate",
                      "8"
                    )}
                    {renderInputField(
                      "socialMedia.PlatformDiversity.points",
                      "Platform Diversity",
                      "4"
                    )}
                    {renderInputField(
                      "socialMedia.FeedPostsThisWeek.points",
                      "Feed Posts",
                      "6"
                    )}
                    {renderInputField(
                      "socialMedia.StoryPostsThisWeek.points",
                      "Story Posts",
                      "4"
                    )}
                    {renderInputField(
                      "socialMedia.WeeklyPostCompletion.points",
                      "Post Completion",
                      "8"
                    )}
                    {renderInputField(
                      "socialMedia.WeeklyGoalCompletion.points",
                      "Goal Completion",
                      "10"
                    )}
                  </div>
                </div>

                {/* Brand Partnerships (30%) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    🤝 Brand Partnerships (30% weight)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputField(
                      "BrandPartnerships.PaidDealValue.point",
                      "Paid Deals",
                      "50"
                    )}
                    {renderInputField(
                      "BrandPartnerships.FreeDeals",
                      "Free Deals",
                      "3"
                    )}
                  </div>
                </div>

                {/* Merch Sales (15%) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    🛍️ Merch Sales (15% weight)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputField(
                      "MerchSales.UnitsSoldThisWeek.point",
                      "Units Sold",
                      "15"
                    )}
                    {renderInputField(
                      "MerchSales.TopSellerBonus.point",
                      "Top Seller Bonus",
                      "10"
                    )}
                  </div>
                </div>

                {/* AI Tool Usage (15%) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    🤖 AI Tool Usage (15% weight)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInputField(
                      "AIToolUsage.PersonalBrandingAITool.point",
                      "Personal Branding",
                      "10"
                    )}
                    {renderInputField(
                      "AIToolUsage.PartnershipsAITool.point",
                      "Partnerships",
                      "7.5"
                    )}
                    {renderInputField(
                      "AIToolUsage.MerchAITool.point",
                      "Merch Tools",
                      "7.5"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-3 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Changes are saved automatically when you click "Save Changes"
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPointsBreakdown(false);
                    setEditingPoints({});
                    setFieldErrors({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePointsUpdate}
                  disabled={
                    pointsUpdateLoading ||
                    Object.keys(editingPoints).length === 0
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-[--primary] border border-transparent rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {pointsUpdateLoading ? (
                    <>
                      <ButtonLoader />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof Link Modal */}
      {Object.keys(showProofLinkModal).map((modalKey) => {
        if (!showProofLinkModal[modalKey]) return null;

        const [goalId, week, taskIndex] = modalKey.split("-");
        const currentProofLink = editingProofLink[modalKey] || "";

        return (
          <div key={modalKey} className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() =>
                  handleCloseProofLinkModal(goalId, week, taskIndex)
                }
              ></div>

              {/* Modal panel */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                {/* Header */}
                <div className="bg-white px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {currentProofLink ? "Edit Proof Link" : "Add Proof Link"}
                    </h3>
                    <button
                      onClick={() =>
                        handleCloseProofLinkModal(goalId, week, taskIndex)
                      }
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FiClose className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white px-6 py-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proof Link URL
                      </label>
                      <input
                        type="url"
                        value={currentProofLink}
                        onChange={(e) => {
                          setEditingProofLink((prev) => ({
                            ...prev,
                            [modalKey]: e.target.value,
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        placeholder="https://instagram.com/p/abc123"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter a URL that proves task completion (optional)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                  <button
                    onClick={() =>
                      handleCloseProofLinkModal(goalId, week, taskIndex)
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveProofLink(goalId, week, taskIndex)}
                    disabled={currentProofLink && !isValidUrl(currentProofLink)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </Wrapper>
  );
};

export default UserGoals;
